import React, { useRef, useState, useEffect } from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { setIsAlertModalVisible } from 'reducers/global.reducer';
import { setSelectedPoint } from 'reducers/awdView.reducer';
import { Coordinates, PlaceTag } from 'interfaces/vector.interface';
import { Layer, MarkerType, MarkerIconType } from 'interfaces/maps.interface';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useSearchParams } from 'customHooks/useSearchParams';

import extractValuesForPlaceTag from 'utils/extractValuesForPlaceTag';
import titleCase from 'utils/titleCase';
import debounce from 'utils/debounce';
import getDuration from 'utils/getDuration';
import { APP_NAMES, isLocalEnv } from 'utils/constants';

import useSeasonalConfigs from 'customHooks/useSeasonalConfigs';
import { redirect } from 'actions/navigation';
import { AppName } from 'interfaces/global.interface';
import { iconSizeToZoomMap, iconsMap } from './constants';

import './index.css';

type MarkerUnitProps = {
	approvalStatus?: string;
	isInteractive?: boolean;
	markerMetaData?: PlaceTag[];
	markerType?: MarkerType;
	placeId?: string;
	position: Coordinates;
	selectedLayer?: Layer;
	showToolTipHeader?: boolean;
	showToolTipOnHover?: boolean;
	updateIsCursorOverPoint?: (a: boolean) => void;
	zoom: number;
};

const getMarkerOptions = (isInteractive: boolean) => ({
	fillOpacity: 0.6,
	strokeColor: 'red',
	strokeOpacity: 1,
	strokeWeight: 0.8,
	zIndex: 1,
	clickable: isInteractive,
});

const MarkerUnit: React.FC<MarkerUnitProps> = ({
	approvalStatus,
	// markerIndex,
	isInteractive = false,
	markerMetaData,
	markerType = 'PRIMARY',
	placeId,
	position,
	selectedLayer,
	showToolTipOnHover = false,
	showToolTipHeader = false,
	updateIsCursorOverPoint,
	zoom,
}): JSX.Element => {
	const infoWindowRef: any = useRef(null);
	const mouseOverInfoWindowCount: any = useRef(0);

	const searchParams = useSearchParams();
	const dispatch = useAppDispatch();
	const path = useAppSelector((s) => s.router.location.pathname);
	const appName = (
		path.includes(APP_NAMES.TELEMATICS)
			? APP_NAMES.TELEMATICS
			: path.includes(APP_NAMES.AWD)
			? APP_NAMES.AWD
			: APP_NAMES.VIEW
	) as AppName;
	// awd
	const selectedPoint = useAppSelector((s) => s.awdView.selectedPoint);
	const pointsOfFarmId = useAppSelector((s) => s.awdView.pointsOfFarmId);
	const editablePolygons = useAppSelector((s) => s.awdView.editablePolygons);
	const isSelectedPolygonSaved = useAppSelector(
		(s) => s.awdView.isSelectedPolygonSaved,
	);
	//
	// telematics
	const liveMachinesData = useAppSelector(
		(s) => s.telematics.liveMachinesData,
	);
	const selectedLiveMachine = useAppSelector(
		(s) => s.telematics.selectedLiveMachine,
	);
	//
	const debugMode =
		`${useAppSelector(
			(s) => s.router.location.query.debug,
		)}`.toLowerCase() === 'true';
	const eventId = useAppSelector((s) => s.router.location.query.eventId);
	const [, seasonId, seasonKey] = useSeasonalConfigs();

	const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);
	const [markerIconType, setMarkerIconType] =
		useState<MarkerIconType>('DEFAULT');

	const { lng, lat } = position;

	const handleInfoWindowUnmount = () => {
		infoWindowRef.current = null;
		mouseOverInfoWindowCount.current = 0;
	};

	const debouncedHandleMouseOut = debounce(() => {
		if (
			(!isInteractive ||
				markerType === 'PRIMARY' ||
				infoWindowRef.current !== null) &&
			mouseOverInfoWindowCount.current > 0
		) {
			return;
		}
		updateIsCursorOverPoint && updateIsCursorOverPoint(false);
		setIsTooltipVisible(false);
	}, 100);

	const handleMouseOver = () => {
		if (!isInteractive || markerType === 'PRIMARY') return;
		updateIsCursorOverPoint && updateIsCursorOverPoint(true);
		setIsTooltipVisible(true);
	};

	const handleClick = () => {
		// TODO: make handleClick a prop and send from parent. Writing functionality based on markerType doesn't seem scalable
		const selectedPointPlaceId = selectedPoint?.split('-')[0];
		if (
			!isInteractive ||
			markerType === 'SECONDARY' ||
			markerType === 'MACHINE-PATH' ||
			selectedPointPlaceId === placeId ||
			selectedLayer === undefined
		) {
			return;
		}
		if (markerType === 'PRIMARY') {
			if (
				!isSelectedPolygonSaved &&
				editablePolygons !== undefined &&
				Object.keys(editablePolygons).length > 0
			) {
				dispatch(setIsAlertModalVisible(true));
				return;
			}
			const key = `${placeId}-${selectedLayer.PlaceNamespace}-${selectedLayer.PlaceSourceType}-${selectedLayer.PlaceVisibleType}`;
			dispatch(setSelectedPoint(key));
			searchParams.set('contentId', pointsOfFarmId[key].contentId);
			const newPath = `awd?${searchParams.toString()}`;
			dispatch(redirect(newPath));
		}
	};

	const getMarkerIcon = (): google.maps.Icon | undefined => {
		if (markerIconType === 'DEFAULT') {
			return undefined;
		}
		const iconSizes = getIconSizes(zoom);
		let iconMeta = {};

		// extra-extra small icons
		if (['INACTIVE-PIPE', 'ROUND-MARKER'].includes(markerIconType))
			iconMeta = {
				...iconSizes.extraExtraSmall,
			};
		// extra small icons
		if (
			[
				'ACTIVE-PIPE',
				'ACCEPTED-PIPE',
				'REJECTED-PIPE',
				'DEFAULT-ARROW',
			].includes(markerIconType)
		)
			iconMeta = {
				...iconSizes.extraSmall,
			};
		// small icons
		else if (
			['ACTIVE-ACCEPTED-PIPE', 'ACTIVE-REJECTED-PIPE'].includes(
				markerIconType,
			)
		)
			iconMeta = {
				...iconSizes.small,
			};
		// medium icons
		else if (['INACTIVE-BUND'].includes(markerIconType))
			iconMeta = {
				...iconSizes.medium,
			};
		// large icons
		else if (
			['ACTIVE-BUND', 'ACCEPTED-BUND', 'REJECTED-BUND'].includes(
				markerIconType,
			)
		) {
			iconMeta = {
				...iconSizes.large,
			};
		}
		// extra large icons
		else if (
			[
				'ACTIVE-ACCEPTED-BUND',
				'ACTIVE-REJECTED-BUND',
				'TRIANGLE-RED',
				'CIRCLE-RED',
				'SQUARE-RED',
				'ARROW-RED',
				'ARROW-YELLOW',
				'CIRCLE-YELLOW',
				'CIRCLE-PINK',
				'ARROW-BLUE',
				'SQUARE-BLUE',
				'ARROW-GREEN',
				'SQUARE-GREEN',
			].includes(markerIconType)
		) {
			iconMeta = {
				...iconSizes.extraLarge,
			};
		}
		// extra-extra large icons
		else if (['OTHER-POINT', 'MACHINE-GENERATED'].includes(markerIconType))
			iconMeta = {
				...iconSizes.extraExtraLarge,
			};

		return {
			url: iconsMap[markerIconType],
			...iconMeta,
		};
	};

	const updateMarkerIconType = (
		approvalStatus: string | undefined,
		markerType: MarkerType,
		placeId: string | undefined,
		selectedLayer: Layer | undefined,
		selectedPoint: string | undefined,
	) => {
		let placeSourceType = selectedLayer?.PlaceSourceType;
		let marker: MarkerIconType = 'DEFAULT';
		if (!placeSourceType) {
			setMarkerIconType('ROUND-MARKER');
			return;
		}
		if (markerType === 'PRIMARY') {
			const selectedPointPlaceId = selectedPoint?.split('-')[0];
			if (
				selectedPointPlaceId !== undefined &&
				selectedPointPlaceId === `${placeId}`
			) {
				// active
				if (placeSourceType === 'PIPE_INSTALLATION_SRC') {
					marker = 'ACTIVE-PIPE';
					if (approvalStatus === 'APPROVED') {
						marker = 'ACTIVE-ACCEPTED-PIPE';
					} else if (approvalStatus === 'REJECT') {
						marker = 'ACTIVE-REJECTED-PIPE';
					}
				} else if (placeSourceType === 'INSIDE_BUND_SRC') {
					marker = 'ACTIVE-BUND';
					if (approvalStatus === 'APPROVED') {
						marker = 'ACTIVE-ACCEPTED-BUND';
					} else if (approvalStatus === 'REJECT') {
						marker = 'ACTIVE-REJECTED-BUND';
					}
				}
			} else if (approvalStatus === 'APPROVED') {
				// inactive
				if (placeSourceType === 'PIPE_INSTALLATION_SRC') {
					marker = 'ACCEPTED-PIPE';
				} else if (placeSourceType === 'INSIDE_BUND_SRC') {
					marker = 'ACCEPTED-BUND';
				}
			} else if (approvalStatus === 'REJECT') {
				// inactive
				if (placeSourceType === 'PIPE_INSTALLATION_SRC') {
					marker = 'REJECTED-PIPE';
				} else if (placeSourceType === 'INSIDE_BUND_SRC') {
					marker = 'REJECTED-BUND';
				}
			} else {
				// inactive
				if (placeSourceType === 'PIPE_INSTALLATION_SRC') {
					marker = 'INACTIVE-PIPE';
				} else if (placeSourceType === 'INSIDE_BUND_SRC') {
					marker = 'INACTIVE-BUND';
				}
			}
		} else if (markerType === 'MACHINE-PATH') {
			marker = 'DEFAULT-ARROW';
		} else {
			// TODO: add placeSourceType for Machine generated
			if (placeSourceType === '') {
				marker = 'MACHINE-GENERATED';
			} else {
				marker = 'OTHER-POINT';
			}
		}
		setMarkerIconType(marker);
	};

	useEffect(() => {
		updateMarkerIconType(
			approvalStatus,
			markerType,
			placeId,
			selectedLayer,
			selectedLiveMachine,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		approvalStatus,
		liveMachinesData,
		markerType,
		placeId,
		selectedLayer,
		selectedLiveMachine,
		selectedPoint,
	]);
	const icon = getMarkerIcon();
	return (
		<>
			<Marker
				position={{ lat: lat, lng: lng }}
				options={getMarkerOptions(isInteractive)}
				onMouseOut={debouncedHandleMouseOut}
				onMouseOver={handleMouseOver}
				onClick={handleClick}
				icon={icon}
			/>
			{showToolTipOnHover &&
				markerMetaData &&
				markerMetaData.length > 0 &&
				isTooltipVisible && (
					<InfoWindow
						position={{ lat: lat, lng: lng }}
						options={{
							disableAutoPan: true,
							zIndex: 2,
							pixelOffset: new google.maps.Size(-10, -20),
							maxWidth: 500,
						}}
						onCloseClick={debouncedHandleMouseOut}
						onUnmount={handleInfoWindowUnmount}
						ref={infoWindowRef}
					>
						<div
							style={{
								width: 300,
							}}
							onMouseEnter={() => {
								mouseOverInfoWindowCount.current += 1;
							}}
							onMouseLeave={() => {
								setIsTooltipVisible(false);
								updateIsCursorOverPoint &&
									updateIsCursorOverPoint(false);
								mouseOverInfoWindowCount.current = 0;
							}}
						>
							{showToolTipHeader
								? getToolTipHeader({
										appName,
										placeId,
										selectedLayer,
								  })
								: null}
							{getToolTipBody(
								appName,
								debugMode,
								placeId,
								markerMetaData,
								seasonKey,
								selectedLayer,
								position,
								seasonId,
								eventId,
							)}
						</div>
					</InfoWindow>
				)}
		</>
	);
};

export default React.memo(MarkerUnit);

const getToolTipHeader = ({
	appName,
	placeId,
	selectedLayer,
}: {
	appName: AppName;
	placeId: string | undefined;
	selectedLayer: Layer | undefined;
}): JSX.Element | null => {
	if (selectedLayer === undefined && placeId === undefined) {
		return null;
	}
	if (appName === APP_NAMES.AWD) {
		return null;
	}
	return null;
};

const getToolTipBody = (
	appName: AppName,
	debugMode: boolean,
	placeId: string | undefined,
	placeTags: PlaceTag[],
	seasonKey: string,
	selectedLayer: Layer | undefined,
	position: Coordinates,
	seasonId: string,
	eventId: string,
) => {
	const tooltipData: {
		label: string;
		value: string[] | string;
		type: 'URL' | 'INFO';
	}[] = getTooltipData(appName, placeTags, seasonKey);
	if (appName === APP_NAMES.AWD) {
		let link = createLink(placeTags, position, seasonId, eventId);
		let imageCategory =
			selectedLayer?.PlaceSourceType === 'PIPE_INSTALLATION_SRC'
				? 'PIPE INSTALLATION'
				: selectedLayer?.PlaceSourceType === 'INSIDE_BUND_SRC'
				? 'BUND INSTALLATION'
				: selectedLayer?.PlaceSourceType ?? 'NA';

		tooltipData.push({ label: 'Verify Point', value: link, type: 'URL' });
		tooltipData.push({
			label: 'Image Category',
			value: imageCategory,
			type: 'INFO',
		});
	}

	if (isLocalEnv || debugMode) {
		tooltipData.push({
			label: 'place_Id',
			value: `${placeId}`,
			type: 'INFO',
		});
	}

	return (
		<ul className="tooltipBodyContainer">
			{tooltipData.map((el) => {
				return (
					<li key={el.label}>
						{titleCase(el.label)}:{' '}
						{el.type === 'URL' ? (
							<a
								href={
									Array.isArray(el.value)
										? el.value[0]
										: el.value
								}
								target="_blank"
							>
								Click here to redirect{' '}
								<OpenInNewIcon
									fontSize="inherit"
									sx={{ marginBottom: '-2px' }}
								/>
							</a>
						) : (
							<b>{`${el.value}`}</b>
						)}
					</li>
				);
			})}
		</ul>
	);
};

const getTooltipData = (
	appName: AppName,
	placeTags: PlaceTag[],
	seasonKey: string,
) => {
	let res: {
		label: string;
		value: string[] | string;
		type: 'URL' | 'INFO';
	}[] = [];
	// TODO: write separate callback functions for each appName to be used inside the forEach
	if (appName === APP_NAMES.AWD) {
		placeTags.forEach(
			({
				TagPropertyKey: tagKey,
				TagName: tagName,
				value: tagValue,
			}: PlaceTag) => {
				if (
					(tagName === 'PROGRAM' && tagKey === 'NAME') ||
					(tagName === 'TagName_FARMER' && tagKey === 'FARMER_ID') ||
					(tagName === 'TagName_FARM' && tagKey === 'FARM_ID') ||
					(tagName === 'TagName_APPROVAL_STATUS' &&
						tagKey === 'STATUS') ||
					(tagName === 'REFERRAL' && tagKey === 'S3_MOBILE') ||
					(tagName === 'PINGS' && tagKey === 'SPRAYER_STATUS') ||
					(tagName === 'PINGS' && tagKey === 'ENGINE_STATUS') ||
					(tagName === 'CONTENT_METADATA' && tagKey === 'CONTENT_ID')
				) {
					// TODO: handle for other program names as well
					if (
						tagName === 'PROGRAM' &&
						tagKey === 'NAME' &&
						(tagValue.stringValues.includes(
							'AWD_22_KHARIF_PROGRAM',
						) ||
							tagValue.stringValues.includes(
								'AWD_22_RABI_PROGRAM',
							) ||
							tagValue.stringValues.includes(
								'AWD_23_RABI_PROGRAM',
							) ||
							tagValue.stringValues.includes(
								'AWD_24_KHARIF_PROGRAM',
							) ||
							tagValue.stringValues.includes(
								'AWD_24_RABI_PROGRAM',
							) ||
							tagValue.stringValues.includes(
								'AWD_25_KHARIF_PROGRAM',
							) ||
							tagValue.stringValues.includes(
								'AWD_25_RABI_PROGRAM',
							) ||
							tagValue.stringValues.includes(
								'AWD_26_KHARIF_PROGRAM',
							))
					) {
						res.push({
							label: 'PROGRAM_NAME',
							value:
								seasonKey === 'KHARIF_2022'
									? 'AWD_22_KHARIF'
									: seasonKey === 'RABI_2022'
									? 'AWD_22_Rabi'
									: seasonKey === 'KHARIF_2024'
									? 'KHARIF_2024'
									: seasonKey === 'RABI_2024'
									? 'AWD_24_Rabi'
									: seasonKey === 'KHARIF_2025'
									? 'KHARIF_2025'
									: seasonKey === 'RABI_2025'
									? 'AWD_25_Rabi'
									: seasonKey === 'KHARIF_2026'
									? 'KHARIF_2026'
									: 'KHARIF_2026',
							type: 'INFO',
						});
					} else if (
						tagName === 'TagName_APPROVAL_STATUS' &&
						tagKey === 'STATUS'
					) {
						res.push({
							label: 'Validation_status',
							value: tagValue.stringValues[0],
							type: 'INFO',
						});
					} else if (
						tagName === 'REFERRAL' &&
						tagKey === 'S3_MOBILE'
					) {
						res.push({
							label: 'Reffered_by_mobile_no.',
							value: `${tagValue.int64Values[0]}`,
							type: 'INFO',
						});
					} else {
						res.push({
							label: tagKey,
							value: extractValuesForPlaceTag(tagValue)[0],
							type: 'INFO',
						});
					}
				}
			},
		);
	} else if (appName === APP_NAMES.TELEMATICS) {
		placeTags.forEach(
			({
				TagPropertyKey: tagKey,
				TagName: tagName,
				value: tagValue,
			}: PlaceTag) => {
				if (tagName === 'IOT_DEVICE') {
					if (tagKey === 'BATTERY_PERCENTAGE') {
						res.push({
							label: tagKey,
							value: `${extractValuesForPlaceTag(tagValue)[0]}%`,
							type: 'INFO',
						});
					} else if (tagKey === 'INTERNAL_TEMPERATURE') {
						res.push({
							label: tagKey,
							value: `${
								extractValuesForPlaceTag(tagValue)[0]
							} \u00B0C`,
							type: 'INFO',
						});
					} else if (tagKey === 'SPEED') {
						res.push({
							label: tagKey,
							value: `${extractValuesForPlaceTag(
								tagValue,
							)[0].toFixed(3)} kmph`,
							type: 'INFO',
						});
					} else if (tagKey === 'ODOMETER') {
						res.push({
							label: tagKey,
							value: `${
								extractValuesForPlaceTag(tagValue)[0]
							} km`,
							type: 'INFO',
						});
					} else if (tagKey === 'INTERNAL_BATTERY_VOLTAGE') {
						res.push({
							label: tagKey,
							value: `${extractValuesForPlaceTag(tagValue)[0]} V`,
							type: 'INFO',
						});
					} else if (tagKey === 'EXTERNAL_VOLTAGE') {
						res.push({
							label: tagKey,
							value: `${extractValuesForPlaceTag(tagValue)[0]} V`,
							type: 'INFO',
						});
					} else if (tagKey === 'LOADED_BATTERY_VOLTAGE') {
						res.push({
							label: tagKey,
							value: `${extractValuesForPlaceTag(tagValue)[0]} V`,
							type: 'INFO',
						});
					} else if (tagKey === 'HEADING_DEGREES') {
						res.push({
							label: tagKey,
							value: `${
								extractValuesForPlaceTag(tagValue)[0]
							} \u00B0`,
							type: 'INFO',
						});
					} else if (tagKey === 'LAST_CONNECTED') {
						const lastConnected =
							Date.now() / 1000 -
							extractValuesForPlaceTag(tagValue)[0];
						res.push({
							label: tagKey,
							value: `${getDuration(lastConnected)} ago`,
							type: 'INFO',
						});
					} else {
						!tagKey.toLowerCase().includes('status') &&
							res.push({
								label: tagKey,
								value: extractValuesForPlaceTag(tagValue)[0],
								type: 'INFO',
							});
					}
				} else if (tagName === 'PINGS') {
					if (tagKey === 'CELLULAR_SIGNAL_STRENGTH') {
						let val = extractValuesForPlaceTag(tagValue)[0];
						val = Number(val).toFixed(2);
						res.push({
							label: tagKey,
							value: `${val}%`,
							type: 'INFO',
						});
					} else if (tagKey === 'SPRAYER_ON') {
						res.push({
							label: tagKey,
							value: `${extractValuesForPlaceTag(tagValue)[0]}`,
							type: 'INFO',
						});
					} else if (tagKey === 'INTERNAL_BATTERY_VOLTAGE') {
						let val = extractValuesForPlaceTag(tagValue)[0];
						val = Number(val).toFixed(2);
						res.push({
							label: tagKey,
							value: `${val} Volts`,
							type: 'INFO',
						});
					} else if (tagKey === 'EXTERNAL_VOLTAGE') {
						let val = extractValuesForPlaceTag(tagValue)[0];
						val = Number(val).toFixed(2);
						res.push({
							label: tagKey,
							value: `${val} Volts`,
							type: 'INFO',
						});
					} else if (tagKey === 'ENGINE_STATUS') {
						res.push({
							label: tagKey,
							value: `${extractValuesForPlaceTag(tagValue)[0]}`,
							type: 'INFO',
						});
					} else if (tagKey === 'INTERNAL_TEMPERATURE') {
						let val = extractValuesForPlaceTag(tagValue)[0];
						val = Number(val).toFixed(2);
						res.push({
							label: tagKey,
							value: `${val} deg C`,
							type: 'INFO',
						});
					}
				}
			},
		);
	}
	res.sort((a, b) => a.label.localeCompare(b.label));
	return res;
};

const createLink = (
	placeTags: PlaceTag[],
	position: Coordinates,
	seasonId: string,
	eventId: string,
) => {
	let newFarmId = '';
	let newContentId = '';
	placeTags.forEach(
		({
			TagPropertyKey: tagKey,
			TagName: tagName,
			value: tagValue,
		}: PlaceTag) => {
			if (tagName === 'TagName_FARM' && tagKey === 'FARM_ID') {
				newFarmId = extractValuesForPlaceTag(tagValue);
			}
			if (tagName === 'CONTENT_METADATA' && tagKey === 'CONTENT_ID') {
				newContentId = extractValuesForPlaceTag(tagValue);
			}
		},
	);
	let link = `${document.URL.split('?')[0]}?lat=${position.lat}&lng=${
		position.lng
	}&farmId=${newFarmId}&eventId=${eventId}&contentId=${newContentId}&seasonId=${seasonId}`;
	return link;
};

const getIconSizes = (zoom: number) => {
	const DEFAULT_ANCHOR_VALUE_FACTOR = 2.5;
	const DEFAULT_ICON_SIZE_FACTOR = 2;
	return {
		extraExtraSmall: {
			anchor: new google.maps.Point(
				parseFloat(
					`${
						6 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
				parseFloat(
					`${
						6 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
			),
			scaledSize: new google.maps.Size(
				12 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
				12 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
			),
		},
		extraSmall: {
			anchor: new google.maps.Point(
				parseFloat(
					`${
						10 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
				parseFloat(
					`${
						10 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
			),
			scaledSize: new google.maps.Size(
				20 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
				20 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
			),
		},
		small: {
			anchor: new google.maps.Point(
				parseFloat(
					`${
						14 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
				parseFloat(
					`${
						14 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
			),
			scaledSize: new google.maps.Size(
				26 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
				26 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
			),
		},
		medium: {
			anchor: new google.maps.Point(
				parseFloat(
					`${
						18 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
				parseFloat(
					`${
						18 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
			),
			scaledSize: new google.maps.Size(
				26 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
				26 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
			),
		},
		large: {
			anchor: new google.maps.Point(
				parseFloat(
					`${
						23 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
				parseFloat(
					`${
						23 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
			),
			scaledSize: new google.maps.Size(
				34 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
				32 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
			),
		},
		extraLarge: {
			anchor: new google.maps.Point(
				parseFloat(
					`${
						28 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
				parseFloat(
					`${
						28 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
			),
			scaledSize: new google.maps.Size(
				40 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
				40 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
			),
		},
		extraExtraLarge: {
			anchor: new google.maps.Point(
				parseFloat(
					`${
						30 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
				parseFloat(
					`${
						30 /
						(iconSizeToZoomMap[`${zoom}`]?.anchorValueFactor ??
							DEFAULT_ANCHOR_VALUE_FACTOR)
					}`,
				),
			),
			scaledSize: new google.maps.Size(
				34 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
				34 /
					(iconSizeToZoomMap[`${zoom}`]?.iconSizeFactor ??
						DEFAULT_ICON_SIZE_FACTOR),
			),
		},
	};
};
